import type {
	FinancialPropertyListItem,
	ServicePeriodListItem,
	ServicePeriodStatusValue,
	UnitPayoutStatusValue,
} from "@/features/financialShared";
import {
	ServicePeriodStatus,
	UnitPayoutStatus,
} from "@/features/financialShared";
import type {
	ChargesPeriodUnitsHeader,
	ChargesUnitListItem,
} from "./chargesManagement.types";

export interface ChargesApproverOption {
	id: string;
	name: string;
}

export interface ChargesManagementPermissions {
	canEdit: boolean;
	canPublish: boolean;
}

export interface GetChargesManagementPropertiesArgs {
	status?: string;
	sortBy?: string;
	sortOrder?: "ASC" | "DESC";
	page?: number;
	pageSize?: number;
	search?: string;
	approverId?: string;
}

export interface ChargesManagementPropertiesResponse {
	pageTitle: string;
	lastSyncLabel: string;
	approverOptions: ChargesApproverOption[];
	items: FinancialPropertyListItem[];
	page: number;
	pageSize: number;
	total: number;
	totalPages: number;
	hasPrevious: boolean;
	hasNext: boolean;
	permissions: ChargesManagementPermissions;
}

export interface GetChargesServicePeriodsArgs {
	propertyId: string;
	page?: number;
	pageSize?: number;
	search?: string;
	status?: string;
	approverId?: string;
	tabId?: string;
}

export interface ChargesServicePeriodsResponse {
	propertyId: string;
	propertyName: string;
	unitCount: number;
	items: ServicePeriodListItem[];
	approverOptions: ChargesApproverOption[];
	page: number;
	pageSize: number;
	total: number;
	totalPages: number;
	emptyMessage: string;
	canCreate: boolean;
	canPublish: boolean;
}

export interface ChargesPeriodFileBody {
	filePath: string;
	fileName: string;
	mimeType: string;
	fileSize: number;
}

export interface CreateChargesServicePeriodBody {
	tabId: string;
	periodStart: string;
	periodEnd: string;
	dueDate: string;
	file?: ChargesPeriodFileBody;
}

export interface GetChargesServicePeriodUnitsArgs {
	propertyId: string;
	periodId: string;
	page?: number;
	pageSize?: number;
	search?: string;
	status?: string;
	paymentMethod?: string;
	sortBy?: string;
	sortOrder?: "ASC" | "DESC";
}

export interface ChargesPeriodUnitsStats {
	totalUnits: number;
	settled: number;
	outstanding: number;
	pastDueOrPartial: number;
}

export interface ChargesServicePeriodUnitsResponse {
	header: ChargesPeriodUnitsHeader;
	stats: ChargesPeriodUnitsStats;
	items: ChargesUnitListItem[];
	page: number;
	pageSize: number;
	total: number;
	totalPages: number;
	emptyMessage: string;
}

export type ChargesUnitsExportScope = "ALL" | "PENDING";

export interface ExportChargesServicePeriodUnitsArgs {
	propertyId: string;
	periodId: string;
	scope: ChargesUnitsExportScope;
}

export interface UploadChargesServicePeriodArgs {
	propertyId: string;
	periodId: string;
	body: { file: ChargesPeriodFileBody };
}

export interface ChargesUnitPathArgs {
	propertyId: string;
	periodId: string;
	unitId: string;
}

export interface ChargesInvoiceDetailsBody {
	totalAmount: number;
	totalVat: number;
	totalToPay: number;
	paymentStatus: string;
	paymentDate: string;
	paymentMethod: string;
	paymentReferenceId: string;
	partialPayment: number;
	remaining: number;
}

export interface ChargesBreakdownItemBody {
	id: string;
	category: string;
	description: string;
	amount: number;
	tax: number;
	currency: string;
}

export interface UpdateChargesUnitInvoiceBody {
	invoiceDetails: ChargesInvoiceDetailsBody;
	chargeBreakdown: ChargesBreakdownItemBody[];
}

export interface UpdateChargesUnitInvoiceArgs extends ChargesUnitPathArgs {
	body: UpdateChargesUnitInvoiceBody;
}

export type { DownloadedFile } from "@/features/financialShared";

export function mapChargesStatusFromApi(
	raw: unknown,
): ServicePeriodStatusValue | null {
	const value = String(raw ?? "")
		.trim()
		.toUpperCase();
	if (value === "DRAFT") return ServicePeriodStatus.DRAFT;
	if (value === "PUBLISHED") return ServicePeriodStatus.PUBLISHED;
	if (value === "COMPLETED") return ServicePeriodStatus.COMPLETED;
	return null;
}

export function mapChargesPaymentStatusToApi(raw: unknown): string {
	const value = String(raw ?? "")
		.trim()
		.toUpperCase()
		.replace(/[\s-]+/g, "_");
	if (value === "SETTLED") return "SETTLED";
	if (value === "PARTIALLY_PAID") return "PARTIALLY_PAID";
	if (value === "OUTSTANDING") return "OUTSTANDING";
	if (value === "PAST_DUE") return "PAST_DUE";
	return value;
}

export function mapChargesPaymentMethodToApi(
	raw: unknown,
): "APP" | "CASH" | "CHEQUE" | "BANK_TRANSFER" | "" {
	const value = String(raw ?? "")
		.trim()
		.toUpperCase()
		.replace(/[\s-]+/g, "_");
	if (value === "APP") return "APP";
	if (value === "CASH") return "CASH";
	if (value === "CHEQUE" || value === "CHECK") return "CHEQUE";
	if (value === "BANK_TRANSFER") return "BANK_TRANSFER";
	return "";
}

export function mapChargesPaymentMethodLabel(raw: unknown): string {
	const value = mapChargesPaymentMethodToApi(raw);
	if (value === "APP") return "App";
	if (value === "CASH") return "Cash";
	if (value === "BANK_TRANSFER") return "Bank Transfer";
	if (value === "CHEQUE") return "Cheque";
	if (typeof raw === "string" && raw.trim()) return raw.trim();
	return "—";
}

export function mapChargesUnitStatusFromApi(
	raw: unknown,
): UnitPayoutStatusValue {
	const value = String(raw ?? "")
		.trim()
		.toUpperCase()
		.replace(/[\s-]+/g, "_");
	if (value === "SETTLED") return UnitPayoutStatus.SETTLED;
	if (value === "IN_PROGRESS") return UnitPayoutStatus.IN_PROGRESS;
	if (value === "HOLD") return UnitPayoutStatus.HOLD;
	if (value === "PARTIALLY_PAID") return UnitPayoutStatus.PARTIALLY_PAID;
	if (value === "PAST_DUE") return UnitPayoutStatus.PAST_DUE;
	if (value === "OUTSTANDING") return UnitPayoutStatus.OUTSTANDING;
	if (value === "CREDIT") return UnitPayoutStatus.CREDIT;
	return UnitPayoutStatus.OUTSTANDING;
}
